import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import '@theme/product-form';
import '@theme/component-quantity-selector';

class HospitalityPicker extends Component {
  #controller;
  #data;
  #selection = [null, null, null];
  #packageId = null;
  #current = null;
  #busy = false;
  #productForm;
  #variantInput;
  #quantity;
  #quantityInput;
  #addButton;
  #buttonText;
  #addLabel;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  #initialize() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const source = this.querySelector('[data-hospitality-data]');
    this.#productForm = this.querySelector('product-form-component');
    this.#quantity = this.querySelector('quantity-selector-component');
    if (!source || !this.#productForm || !this.#quantity) return;
    this.#data = JSON.parse(source.textContent || '{}');
    this.#variantInput = this.#productForm.querySelector('input[name="id"]');
    this.#quantityInput = this.#quantity.querySelector('input[name="quantity"]');
    this.#addButton = this.#productForm.querySelector('[ref="addToCartButton"]');
    if (!this.#variantInput || !this.#quantityInput || !this.#addButton) return;
    this.#quantityInput.required = true;
    this.#buttonText = [...this.#addButton.childNodes].find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
    this.#addLabel = this.#buttonText?.textContent || '';
    this.#busy = false;

    this.addEventListener('click', this.#onChoice, { signal });
    this.addEventListener('input', this.#onQuantityInput, { signal });
    this.addEventListener('change', this.#onQuantityInput, { signal });
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    this.#render();
    this.#syncVariant();
  }

  #available(variant) {
    return Boolean(variant?.id && variant.available && (this.#maximum(variant) ?? Infinity) >= Number(variant.min || 1));
  }

  #maximum(variant) {
    if (!variant) return null;
    let maximum = variant.max == null ? null : Number(variant.max);
    if (variant.inventoryManagement && variant.inventoryPolicy === 'deny') {
      maximum = Math.min(maximum ?? Infinity, Math.max(0, Number(variant.inventoryQuantity)));
    }
    return maximum;
  }

  #values(index) {
    return [...new Set(this.#data.variants.filter((variant) =>
      this.#available(variant) && this.#selection.slice(0, index).every((value, prior) => variant.options[prior] === value)
    ).map((variant) => variant.options[index]).filter(Boolean))];
  }

  #renderButtons(container, values, index) {
    const existing = [...container.querySelectorAll('button')];
    if (existing.length !== values.length || existing.some((button, position) => button.dataset.value !== values[position])) {
      container.replaceChildren(...values.map((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = value;
        button.dataset.value = value;
        button.dataset.hpOption = String(index);
        return button;
      }));
    }
    for (const button of container.querySelectorAll('button')) {
      const selected = this.#selection[index] === button.dataset.value;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    }
  }

  #render() {
    this.#renderButtons(this.querySelector('[data-hp-days]'), this.#values(0), 0);
    this.querySelector('[data-hp-time]').hidden = !this.#selection[0];
    this.querySelector('[data-hp-space]').hidden = !this.#selection[1];
    this.#renderButtons(this.querySelector('[data-hp-times]'), this.#selection[0] ? this.#values(1) : [], 1);
    this.#renderButtons(this.querySelector('[data-hp-spaces]'), this.#selection[1] ? this.#values(2) : [], 2);
    for (const button of this.querySelectorAll('[data-package-id]')) {
      const variant = this.#data.packages.find((entry) => String(entry.id) === button.dataset.packageId);
      const selected = this.#packageId === button.dataset.packageId;
      button.disabled = !this.#available(variant);
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    }
  }

  #onChoice = (event) => {
    if (!(event.target instanceof Element) || this.#busy) return;
    const button = event.target.closest('[data-hp-option], [data-package-id]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button) || button.disabled) return;
    if (button.hasAttribute('data-package-id')) {
      this.#packageId = button.dataset.packageId;
      this.#selection = [null, null, null];
    } else {
      const index = Number(button.dataset.hpOption);
      this.#packageId = null;
      this.#selection[index] = button.dataset.value;
      this.#selection.fill(null, index + 1);
    }
    this.#clearError();
    this.#render();
    this.#syncVariant();
  };

  #syncVariant() {
    const variant = this.#packageId
      ? this.#data.packages.find((entry) => String(entry.id) === this.#packageId)
      : this.#data.variants.find((entry) => this.#selection.every((value, index) => value && entry.options[index] === value));
    this.#current = this.#available(variant) ? variant : null;
    this.#variantInput.value = this.#current ? String(this.#current.id) : '';
    if (this.#current) {
      const current = this.#current;
      const desiredQuantity = this.#quantity.getValue();
      this.#productForm.dataset.productId = String(current.productId || this.#data.productId);
      this.#productForm.dataset.productUrl = current.productUrl || this.#data.productUrl;
      this.#productForm.dataset.quantityDefault = String(current.min || 1);
      this.#quantity.dataset.variantId = String(current.id);
      this.#quantityInput.dataset.min = String(current.min || 1);
      this.#quantity.setCartQuantity(Number(current.cartQuantity || 0));
      this.#quantity.setValue(desiredQuantity);
      const maximum = this.#maximum(current);
      this.#quantity.updateConstraints(String(current.min || 1), maximum == null ? null : String(maximum), String(current.increment || 1));
      const minimum = Number(current.min || 1);
      const increment = Number(current.increment || 1);
      const constrainedValue = Number(this.#quantity.getValue());
      if ((constrainedValue - minimum) % increment !== 0) {
        this.#quantity.setValue(String(minimum + Math.floor((constrainedValue - minimum) / increment) * increment));
        this.#quantity.updateButtonStates();
      }
    }
    this.#update();
  }

  #onQuantityInput = (event) => {
    if (event.target !== this.#quantityInput) return;
    this.#clearError();
    this.#update();
  };

  #onQuantityUpdate = (event) => {
    if (event.target !== this.#quantity) return;
    this.#clearError();
    const { signal } = this.#controller;
    queueMicrotask(() => {
      if (!signal.aborted && this.isConnected) this.#update();
    });
  };

  #update() {
    const strings = this.#data.strings;
    const variant = this.#current;
    this.#quantityInput.disabled = !variant || this.#busy;
    const quantity = Number(this.#quantityInput.value);
    const validQuantity = Number.isInteger(quantity) && quantity > 0 && this.#quantityInput.validity.valid;
    const cartValidation = this.#quantity.canAddToCart();
    const withinMaximum = !variant || cartValidation.canAdd;
    const maximum = this.#maximum(variant);
    this.#variantInput.disabled = !variant;
    this.#quantity.updateButtonStates();
    if (variant && maximum != null && quantity + Number(variant.increment || 1) > maximum - cartValidation.cartQuantity) {
      this.#quantity.querySelector('[ref="plusButton"]').disabled = true;
    }
    if (!variant || this.#busy) {
      this.#quantity.querySelector('[ref="minusButton"]').disabled = true;
      this.#quantity.querySelector('[ref="plusButton"]').disabled = true;
    }
    this.#addButton.disabled = !variant || !validQuantity || !withinMaximum || this.#busy;
    this.#productForm.setAttribute('aria-busy', String(this.#busy));
    this.querySelector('[data-hp-choices]').disabled = this.#busy;
    if (this.#buttonText) this.#buttonText.textContent = this.#busy ? strings.adding : this.#addLabel;

    this.querySelector('[data-summary-day]').textContent = this.#packageId ? (variant?.label || strings.toChoose) : (this.#selection[0] || strings.toChoose);
    this.querySelector('[data-summary-time]').textContent = this.#packageId ? strings.included : (this.#selection[1] || strings.toChoose);
    this.querySelector('[data-summary-space]').textContent = this.#packageId ? strings.included : (this.#selection[2] || strings.toChoose);
    this.querySelector('[data-total]').textContent = variant && validQuantity
      ? new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency }).format(Number(variant.price) * quantity / 100)
      : '—';
    const rules = [];
    if (variant?.min > 1) rules.push(strings.minimum.replace('__MIN__', variant.min));
    if (variant?.increment > 1) rules.push(strings.increment.replace('__STEP__', variant.increment));
    if (maximum != null) rules.push(strings.maximum.replace('__MAX__', maximum));
    const rulesElement = this.querySelector('[data-quantity-rules]');
    rulesElement.textContent = rules.join(' · ');
    rulesElement.hidden = !rules.length;

    let message = strings.ready;
    if (this.#busy) message = strings.adding;
    else if (!variant) message = this.#packageId || this.#selection.every(Boolean) ? strings.unavailable
      : !this.#selection[0] ? strings.chooseDay : !this.#selection[1] ? strings.chooseTime : strings.chooseSpace;
    else if (!validQuantity) message = strings.invalidQuantity;
    else if (!withinMaximum) message = strings.quantityLimit;
    this.querySelector('[data-message]').textContent = message;
  }

  #onCartUpdate = (event) => {
    if (!event.promise) return;
    const ownAddition = event.target === this.#productForm && event.action === 'add';
    const controller = this.#controller;
    if (ownAddition) {
      this.#busy = true;
      this.#update();
    }
    event.promise.then(({ detail }) => {
      if (controller.signal.aborted || !this.isConnected) return;
      if (Array.isArray(detail?.items)) {
        const quantities = new Map();
        for (const item of detail.items) {
          const id = String(item.variant_id);
          quantities.set(id, (quantities.get(id) || 0) + Number(item.quantity));
        }
        for (const variant of [...this.#data.variants, ...this.#data.packages]) {
          variant.cartQuantity = quantities.get(String(variant.id)) || 0;
        }
      }
      if (ownAddition) this.#busy = false;
      this.#syncVariant();
    }).catch(() => {
      if (controller.signal.aborted || !this.isConnected || !ownAddition) return;
      this.#busy = false;
      this.#update();
      const error = this.#productForm.querySelector('[ref="addToCartTextError"]');
      if (error) {
        error.classList.remove('hidden');
        const text = error.childNodes[2];
        if (text) text.textContent = this.#data.strings.addError;
        else error.appendChild(document.createTextNode(this.#data.strings.addError));
      }
      this.#productForm.querySelector('[ref="liveRegion"]').textContent = this.#data.strings.addError;
    });
  };

  #clearError() {
    this.#productForm.querySelector('[ref="addToCartTextError"]')?.classList.add('hidden');
  }
}

if (!customElements.get('hospitality-picker')) {
  customElements.define('hospitality-picker', HospitalityPicker);
}
